/**
 * Grade-color tokens for the dossier Performance section.
 *
 * §5.BT §3.3 P3 Commit C (2026-06-19). Locked in
 *   docs/audits/2026-06-18_5bt_p3_3_dossier_p2_design.md §E
 * and the parallel design-system.md addition shipped in the same commit.
 *
 * §5.BT §3.3 polish iteration 1 (2026-06-19, Session 012): B-grade re-tokenized to
 *   visibly differentiate from A-grade. Old B: fg #16A34A / bg #DCFCE7 (mint-100).
 *   Iteration 1 B: fg #15803D (green-700) / bg #BBF7D0 (green-200). Stayed in green
 *   family — Ryan's Track A re-verify confirmed A and B still read as "two shades
 *   of green" rather than as distinct categorical signals.
 *
 * §5.BT §3.3 polish iteration 2 (2026-06-19, Session 013): B-grade re-tokenized
 *   to BLUE per procurement-grading convention (A=green, B=blue, C=amber,
 *   D=orange-red, F=dark-red). The same-hue-different-shade approach failed the
 *   scanning-distance test; hue-distinct treatment locks the categorical signal.
 *   New B: fg #1D4ED8 (Tailwind blue-700) / bg #DBEAFE (Tailwind blue-100).
 *   Blue signals "solid, good, not best" while remaining categorically distinct
 *   from A's green at any scanning distance. No collision with C amber, D coral,
 *   or F dark-red.
 *
 * Five categorical procurement signals (worst is `F`):
 *   A — outstanding (deep emerald on near-white mint)
 *   B — solid       (blue-700 on blue-100 — hue-distinct from A per 2026-06-19 iter 2)
 *   C — watch       (warning yellow)
 *   D — concern     (coral / orange-red — `danger`)
 *   F — failure     (dark red — `dangerDeep`, NEW token §E)
 *
 * F is a categorical signal — supplier should be evaluated for off-boarding
 * or explicit remediation governance — distinct from D's gradient continuation
 * of declining performance.
 */
import { colors } from '../SharedComponents';

// New token: dark-red for categorical F-grade failure. Distinct from `colors.danger`
// (D-grade). Token name mirrors `dangerLight` / `successLight` etc.
export const GRADE_DANGER_DEEP = '#991B1B';
export const GRADE_DANGER_DEEP_LIGHT = '#FEE2E2';

export const GRADE_COLORS = {
  A: { fg: colors.success, bg: colors.successLight, label: 'Outstanding' },
  B: { fg: '#1D4ED8',      bg: '#DBEAFE',           label: 'Solid' },
  C: { fg: colors.warning, bg: colors.warningLight, label: 'Watch' },
  D: { fg: colors.danger,  bg: colors.dangerLight,  label: 'Concern' },
  F: { fg: GRADE_DANGER_DEEP, bg: GRADE_DANGER_DEEP_LIGHT, label: 'Failure' },
};

const NEUTRAL = { fg: colors.textMuted, bg: colors.lightGray, label: 'No data' };

/**
 * Resolve grade colors. Returns the neutral muted style for unknown / null grades
 * so the UI degrades gracefully without throwing.
 */
export function getGradeColor(grade) {
  if (!grade) return NEUTRAL;
  return GRADE_COLORS[grade] || NEUTRAL;
}
